import { BaseService } from './base.service';
import {
  CreateStaffTopicInput,
  InboxAssignee,
  InboxConflict,
  InboxListParams,
  InboxListResponse,
  InboxLocation,
  InboxTopic,
  NoteSource,
  StaffThread,
  ThreadMessage,
} from '@/types/conversation';

export const INBOX_PAGE_SIZE = 50;

class InboxConversationService extends BaseService {
  constructor() {
    super('/staff/conversations');
  }

  async listTopics(params: InboxListParams): Promise<InboxListResponse> {
    const query = new URLSearchParams();
    query.set('locationId', params.locationId);
    query.set('scope', params.scope.join(','));
    query.set('status', params.status);
    query.set('type', params.type);
    if (params.unreadOnly) query.set('unreadOnly', 'true');
    if (params.q) query.set('q', params.q);
    if (params.memberId) query.set('memberId', params.memberId);
    query.set('page', String(params.page));
    query.set('pageSize', String(INBOX_PAGE_SIZE));
    return this.get<InboxListResponse>(`?${query.toString()}`);
  }

  async getUnreadCount(locationId?: string): Promise<{ count: number }> {
    const qs = locationId
      ? `?${new URLSearchParams({ locationId }).toString()}`
      : '';
    return this.get<{ count: number }>(`/unread-count${qs}`);
  }

  async getLocations(): Promise<InboxLocation[]> {
    return this.get<InboxLocation[]>('/locations');
  }

  async getAssignees(locationId: string): Promise<InboxAssignee[]> {
    return this.get<InboxAssignee[]>(
      `/assignees?${new URLSearchParams({ locationId }).toString()}`,
    );
  }

  async listForMember(memberId: string): Promise<InboxTopic[]> {
    return this.get<InboxTopic[]>(`/member/${memberId}`);
  }

  async getThread(id: string): Promise<StaffThread> {
    return this.get<StaffThread>(`/${id}`);
  }

  async markRead(id: string): Promise<{ success: true; lastReadAt: string }> {
    return this.post<{ success: true; lastReadAt: string }>(`/${id}/read`);
  }

  async reply(id: string, body: string): Promise<ThreadMessage> {
    return this.post<ThreadMessage>(`/${id}/messages`, { body });
  }

  async replyToNote(
    source: NoteSource,
    noteId: string,
    body: string
  ): Promise<StaffThread> {
    return this.post<StaffThread>(
      `/notes/${encodeURIComponent(source)}/${encodeURIComponent(noteId)}/messages`,
      { body }
    );
  }

  async createTopic(input: CreateStaffTopicInput): Promise<StaffThread> {
    return this.post<StaffThread>('', input);
  }

  async claim(
    id: string,
    expectedAssigneeId: string | null,
  ): Promise<StaffThread> {
    return this.post<StaffThread>(`/${id}/claim`, { expectedAssigneeId });
  }

  async reassign(
    id: string,
    assigneeId: string,
    expectedAssigneeId: string | null,
  ): Promise<StaffThread> {
    return this.post<StaffThread>(`/${id}/reassign`, {
      assigneeId,
      expectedAssigneeId,
    });
  }

  async unassign(
    id: string,
    expectedAssigneeId: string | null,
  ): Promise<StaffThread> {
    return this.post<StaffThread>(`/${id}/unassign`, { expectedAssigneeId });
  }

  async close(id: string): Promise<StaffThread> {
    return this.post<StaffThread>(`/${id}/close`, { expectedStatus: 'active' });
  }

  async complete(id: string): Promise<StaffThread> {
    return this.post<StaffThread>(`/${id}/complete`, {
      expectedStatus: 'active',
    });
  }

  async addAttempt(id: string, body: string): Promise<StaffThread> {
    return this.post<StaffThread>(`/${id}/attempts`, { body });
  }

  async reopen(id: string): Promise<StaffThread> {
    return this.post<StaffThread>(`/${id}/reopen`, {
      expectedStatus: 'closed',
    });
  }
}

export const inboxConversationService = new InboxConversationService();

/** Extracts the INBOX_CONFLICT payload from a 409 axios error, else null. */
export function getInboxConflict(error: unknown): InboxConflict | null {
  const response = (error as { response?: { status?: number; data?: any } })
    ?.response;
  if (!response || response.status !== 409) return null;
  const data = response.data;
  if (!data || data.code !== 'INBOX_CONFLICT' || !data.conflict) return null;
  return {
    action: data.conflict.action,
    actorName: data.conflict.actorName,
    thread: data.thread,
  };
}
